/**
 * How many device pixels a 3D surface draws per CSS pixel — the one place the
 * port decides how much resolution a picture is worth.
 *
 * ## What this is not
 *
 * It is **not** a fix for a letterboxed or upscaled viewport, because there is
 * no such thing here to fix: `/fly`'s canvas is `width: 100%; height: 100%` of
 * an `inset: 0` host (`app.css`'s `.flight__canvas-host`), it carries no
 * `image-rendering`, and `flight-view.ts`'s `resizeView` sizes the drawing
 * buffer from the container's own CSS box. Measured on a real Blackwell GPU
 * over the live `/fly` swapchain, the canvas' `width`/`height` came back as
 * exactly `floor(cssWidth x devicePixelRatio)` at every ratio tried — 1280x800
 * at 1, 1471x919 at 1.15, 2560x1600 at 2. The picture was already native.
 *
 * ## What it is
 *
 * The **cap**, and only the cap. Every surface used to carry
 * `Math.min(window.devicePixelRatio, 2)` unconditionally, so the one and only
 * display on which the port drew fewer pixels than the panel has is one with a
 * ratio above 2: at `devicePixelRatio: 3` the same 1280x800 window censused
 * 2560x1600 rather than the 3840x2400 it could have had — **44% of the
 * fragments thrown away**. That is the whole of "not native", and it is what
 * `ENHANCED MODELS` now lifts.
 *
 * ## Why a cap existed at all, and why it stays as the off state
 *
 * Fragment cost is quadratic in the ratio, and the port targets arbitrary
 * browsers on arbitrary hardware, so an uncapped ratio is a promise this code
 * cannot keep on every machine — a `dpr: 3` panel is 2.25x the fragments of the
 * capped picture for the same window. Keeping 2 as the *floor of the promise*
 * and letting a setting lift it is the arrangement that costs nothing on the
 * overwhelmingly common `dpr: 1`/`dpr: 2` display (where `min(dpr, 2)` and `dpr`
 * are the same number and this function is a no-op) while giving a high-DPI
 * panel the pixels it has.
 *
 * ## Kept three.js-free
 *
 * For `polygon-outlines.ts`' reason: the arithmetic is the part worth pinning
 * and it is testable without a GPU. `PixelRatioTarget` is structural, so a
 * `WebGPURenderer` satisfies it without this module importing three.
 */

/**
 * The ratio a picture is capped at when the setting is off — the ceiling every
 * 3D surface in this app used to carry unconditionally.
 *
 * Two rather than some other number because it is the ratio of every ordinary
 * high-DPI display, so it is the largest cap that is free on the hardware most
 * players have and still bounds the cost on the hardware they do not.
 */
export const AUTHENTIC_PIXEL_RATIO_CAP = 2;

/** `globalThis.devicePixelRatio` where there is a window, and 1 where there is not (tests, workers). */
function currentDevicePixelRatio(): number {
  const ratio = (globalThis as { devicePixelRatio?: number }).devicePixelRatio;
  return typeof ratio === "number" && Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
}

/**
 * The drawing-buffer scale for a surface, given whether the player asked for
 * native resolution.
 *
 * A non-finite or non-positive `devicePixelRatio` reads as 1 rather than
 * throwing: it is a number this code gets from the browser and never validates
 * upstream, and a `NaN` reaching `setPixelRatio` would size the canvas to
 * `NaN` and blank the view.
 */
export function viewPixelRatio(nativeResolution: boolean, devicePixelRatio = currentDevicePixelRatio()): number {
  const ratio = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  return nativeResolution ? ratio : Math.min(ratio, AUTHENTIC_PIXEL_RATIO_CAP);
}

/** What `applyViewPixelRatio` needs of a renderer — three's `WebGPURenderer` satisfies it structurally. */
export interface PixelRatioTarget {
  getPixelRatio(): number;
  setPixelRatio(value: number): void;
}

/**
 * Applies the policy to a live renderer, and reports whether the buffer scale
 * actually moved.
 *
 * The boolean is the useful half: three's own `setPixelRatio` re-runs `setSize`
 * for you, but the things a resize *notifies* do not know that happened, and on
 * `/fly` that list is not empty (`scene-boot.ts`'s `resizeViewport`). Callers use
 * the return value to run that path exactly when it is needed and never
 * otherwise.
 *
 * Proving that list was complete is what found the one real bug in this area,
 * and it was **not** in this file: the terrain hairlines were sized against the
 * drawing buffer where three wants CSS pixels, so a road already thickened with
 * the pixel ratio before any of this existed — see `polygon-outlines.ts`'s
 * `originalPixelWidth`.
 */
export function applyViewPixelRatio(
  target: PixelRatioTarget,
  nativeResolution: boolean,
  devicePixelRatio = currentDevicePixelRatio(),
): boolean {
  const next = viewPixelRatio(nativeResolution, devicePixelRatio);
  if (target.getPixelRatio() === next) return false;
  target.setPixelRatio(next);
  return true;
}
